import { getElement, AuthService } from './services';
import { i18n } from './i18n';

// ============================================================
// PHOTO LIGHTBOX COMPONENT
// ============================================================

/**
 * Full-screen photo lightbox for viewing images.
 * Supports navigation between multiple photos.
 */
export class PhotoLightbox {
    private overlay: HTMLElement;
    private image: HTMLImageElement;
    private counter: HTMLElement;
    private photos: string[] = [];
    private currentIndex: number = 0;

    constructor() {
        this.overlay = document.createElement('div');
        this.overlay.className = 'photo-lightbox';
        this.overlay.innerHTML = `
            <button class="lightbox-close" aria-label="Close">&times;</button>
            <button class="lightbox-nav lightbox-prev" aria-label="Previous">&lsaquo;</button>
            <img class="lightbox-image" src="" alt="Photo">
            <button class="lightbox-nav lightbox-next" aria-label="Next">&rsaquo;</button>
            <div class="lightbox-counter">1 / 1</div>
        `;
        document.body.appendChild(this.overlay);

        this.image = this.overlay.querySelector('.lightbox-image')!;
        this.counter = this.overlay.querySelector('.lightbox-counter')!;

        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        this.overlay.querySelector('.lightbox-close')?.addEventListener('click', () => this.close());
        this.overlay.querySelector('.lightbox-prev')?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigate(-1);
        });
        this.overlay.querySelector('.lightbox-next')?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigate(1);
        });
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) this.close();
        });
        document.addEventListener('keydown', (e) => {
            if (!this.overlay.classList.contains('active')) return;
            if (e.key === 'Escape') this.close();
            if (e.key === 'ArrowLeft') this.navigate(-1);
            if (e.key === 'ArrowRight') this.navigate(1);
        });
    }

    open(photos: string[], startIndex: number = 0): void {
        this.photos = photos;
        this.currentIndex = startIndex;
        this.updateImage();
        this.overlay.classList.add('active');
        document.body.style.overflow = 'hidden';
    }

    close(): void {
        this.overlay.classList.remove('active');
        document.body.style.overflow = '';
    }

    private navigate(direction: number): void {
        if (this.photos.length === 0) return;
        this.currentIndex = (this.currentIndex + direction + this.photos.length) % this.photos.length;
        this.updateImage();
    }

    private updateImage(): void {
        this.image.src = this.photos[this.currentIndex];
        this.counter.textContent = `${this.currentIndex + 1} / ${this.photos.length}`;
    }
}

// ============================================================
// IMAGE UPLOAD COMPONENT
// ============================================================

export interface ChatImageData {
    data: string;  // base64-encoded image data
    mime_type: string;
}

export interface ChatAudioData {
    data: string;  // base64-encoded audio data
    mime_type: string;
    duration_seconds: number;
}

/**
 * Self-contained image upload component.
 * Handles image selection, compression, preview, and removal.
 */
export class ImageUpload {
    private uploadBtn: HTMLButtonElement;
    private input: HTMLInputElement;
    private previewContainer: HTMLElement;
    private preview: HTMLImageElement;
    private removeBtn: HTMLButtonElement;
    private pendingImage: ChatImageData | null = null;

    constructor() {
        this.uploadBtn = getElement<HTMLButtonElement>('imageUploadBtn');
        this.input = getElement<HTMLInputElement>('imageInput');
        this.previewContainer = getElement<HTMLElement>('imagePreviewContainer');
        this.preview = getElement<HTMLImageElement>('imagePreview');
        this.removeBtn = getElement<HTMLButtonElement>('imageRemoveBtn');

        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        this.uploadBtn.addEventListener('click', () => this.input.click());
        this.input.addEventListener('change', (e) => this.handleSelect(e));
        this.removeBtn.addEventListener('click', () => this.clear());
    }

    /**
     * Get the pending image data for sending with chat.
     */
    getImage(): ChatImageData | null {
        return this.pendingImage;
    }

    /**
     * Get the preview image data URL (for display in chat message).
     */
    getPreviewDataUrl(): string | null {
        return this.pendingImage ? this.preview.src : null;
    }

    /**
     * Clear the pending image and reset UI.
     */
    clear(): void {
        this.pendingImage = null;
        this.preview.src = '';
        this.previewContainer.classList.add('hidden');
        this.uploadBtn.classList.remove('has-image');
        this.input.value = '';
    }

    private async handleSelect(e: Event): Promise<void> {
        const input = e.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        // Accept any image/* type - Safari will auto-convert HEIC to JPEG
        if (!file.type.startsWith('image/')) {
            alert(i18n.imageSelectFile);
            return;
        }

        // Validate file size (max 20MB before compression)
        const maxSize = 20 * 1024 * 1024;
        if (file.size > maxSize) {
            alert(i18n.imageTooLarge);
            return;
        }

        // Compress and resize the image
        const compressed = await this.compressImage(file);
        if (!compressed) {
            alert(i18n.imageProcessError);
            return;
        }

        const { dataUrl, mimeType } = compressed;

        // Extract base64 data (remove the data:image/xxx;base64, prefix)
        const base64Data = dataUrl.split(',')[1];
        if (!base64Data) {
            alert(i18n.imageReadError);
            return;
        }

        this.pendingImage = {
            data: base64Data,
            mime_type: mimeType
        };

        // Show preview
        this.preview.src = dataUrl;
        this.previewContainer.classList.remove('hidden');
        this.uploadBtn.classList.add('has-image');

        // Reset the input so the same file can be selected again
        input.value = '';
    }

    /**
     * Compress and resize an image to reduce upload size.
     */
    private compressImage(file: File, maxDimension = 1920, quality = 0.8): Promise<{dataUrl: string, mimeType: string} | null> {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                let { width, height } = img;

                // Calculate new dimensions if image is larger than maxDimension
                if (width > maxDimension || height > maxDimension) {
                    if (width > height) {
                        height = Math.round((height * maxDimension) / width);
                        width = maxDimension;
                    } else {
                        width = Math.round((width * maxDimension) / height);
                        height = maxDimension;
                    }
                }

                // Create canvas and draw resized image
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve(null);
                    return;
                }

                ctx.drawImage(img, 0, 0, width, height);

                // Export as JPEG (good compression, widely supported)
                const dataUrl = canvas.toDataURL('image/jpeg', quality);
                resolve({ dataUrl, mimeType: 'image/jpeg' });
            };

            img.onerror = () => resolve(null);

            // Load the file into the image
            const reader = new FileReader();
            reader.onload = (e) => {
                img.src = e.target?.result as string;
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
        });
    }
}

// ============================================================
// AUTH MODAL COMPONENT
// ============================================================

export interface AuthModalStrings {
    authModalTitle: string;
    authModalDesc: string;
    authModalSubmit: string;
    authModalError: string;
    changeTokenBtn: string;
}

/**
 * Auth modal component - handles only the UI.
 * Delegates token validation and storage to AuthService.
 */
export class AuthModal {
    private modalOverlay: HTMLElement;
    private modalTitle: HTMLElement;
    private modalDesc: HTMLElement;
    private tokenInput: HTMLInputElement;
    private toggleVisibility: HTMLButtonElement;
    private errorEl: HTMLElement;
    private submitBtn: HTMLButtonElement;
    private closeBtn: HTMLButtonElement;
    private changeTokenBtn: HTMLButtonElement;

    private authResolve: ((token: string) => void) | null = null;

    constructor(
        private authService: AuthService,
        private getStrings: () => AuthModalStrings,
        private onTokenChanged?: () => Promise<void>
    ) {
        this.modalOverlay = getElement<HTMLElement>('authModalOverlay');
        this.modalTitle = getElement<HTMLElement>('authModalTitle');
        this.modalDesc = getElement<HTMLElement>('authModalDesc');
        this.tokenInput = getElement<HTMLInputElement>('authTokenInput');
        this.toggleVisibility = getElement<HTMLButtonElement>('authToggleVisibility');
        this.errorEl = getElement<HTMLElement>('authError');
        this.submitBtn = getElement<HTMLButtonElement>('authSubmitBtn');
        this.closeBtn = getElement<HTMLButtonElement>('authCloseBtn');
        this.changeTokenBtn = getElement<HTMLButtonElement>('changeTokenBtn');

        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        this.submitBtn.addEventListener('click', () => this.handleSubmit());
        this.tokenInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.handleSubmit();
            }
        });
        this.toggleVisibility.addEventListener('click', () => this.toggleTokenVisibility());
        this.closeBtn.addEventListener('click', () => this.hideModal());
        this.changeTokenBtn.addEventListener('click', () => this.showModal(true));
    }

    /**
     * Get the current auth token (delegates to service).
     */
    getToken(): string {
        return this.authService.getToken();
    }

    /**
     * Validate auth token. If no valid token, prompts user.
     * Returns the valid token.
     */
    async validate(): Promise<string> {
        // Try to restore from storage
        const restored = await this.authService.tryRestoreToken();
        if (restored) {
            return restored;
        }

        // Prompt for token via modal
        return this.promptForToken();
    }

    private async promptForToken(): Promise<string> {
        return new Promise<string>((resolve) => {
            this.authResolve = (token: string) => {
                this.authService.setToken(token);
                resolve(token);
            };
            this.showModal();
        });
    }

    private showModal(allowClose: boolean = false): void {
        const strings = this.getStrings();
        this.modalTitle.textContent = strings.authModalTitle;
        this.modalDesc.textContent = strings.authModalDesc;
        this.submitBtn.textContent = strings.authModalSubmit;
        this.errorEl.textContent = strings.authModalError;
        this.errorEl.classList.add('hidden');
        // Pre-fill current token when changing, clear when initial auth
        this.tokenInput.value = allowClose ? this.authService.getToken() : '';
        this.tokenInput.type = 'password';
        // Show close button only when changing token (not initial auth)
        if (allowClose) {
            this.closeBtn.classList.remove('hidden');
        } else {
            this.closeBtn.classList.add('hidden');
        }
        this.modalOverlay.classList.add('active');
        // Focus input after animation
        setTimeout(() => this.tokenInput.focus(), 200);
    }

    private hideModal(): void {
        this.modalOverlay.classList.remove('active');
    }

    private async handleSubmit(): Promise<void> {
        const token = this.tokenInput.value.trim();
        if (!token) {
            this.errorEl.classList.remove('hidden');
            return;
        }

        this.submitBtn.disabled = true;
        const isValid = await this.authService.validateToken(token);
        this.submitBtn.disabled = false;

        if (isValid) {
            this.hideModal();
            if (this.authResolve) {
                this.authResolve(token);
                this.authResolve = null;
            } else {
                // Called from change token button - update and notify
                this.authService.setToken(token);
                if (this.onTokenChanged) {
                    await this.onTokenChanged();
                }
            }
        } else {
            this.errorEl.classList.remove('hidden');
            this.tokenInput.select();
        }
    }

    private toggleTokenVisibility(): void {
        if (this.tokenInput.type === 'password') {
            this.tokenInput.type = 'text';
        } else {
            this.tokenInput.type = 'password';
        }
    }
}
